// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { ReactNode } from 'react';
import {
  Image,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type ImageStyle,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Text } from '@/components/Themed';
import GameWeightControl from '@/components/GameWeightControl';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import { clipMeritLadder, formatMeritLadder, meritsCaptionKey } from '@/features/game-definitions/merits';
import type { GameDefinition } from '@/features/game-definitions/types';
import { t } from '@/i18n';
import appColors from '@/theme/colors';
import { createGameImageResolver } from './gameImage';

/**
 * The most ladder a tile prints. A phone-width tile holds three or four rungs a line beside the
 * weight chip, so twelve is three or four lines — about as much as a tile can carry before the
 * ladder outgrows the artwork above it. A bigger field gets the top of the ladder and a count of
 * the places left out; the setup modal behind the info button lists every one.
 */
const MAX_TILE_LADDER_RUNGS = 12;

const NBSP = '\u00A0';
// A line may only break after a slash: every wrapped line then ends "… /" and the next starts on a
// number, rather than stranding a slash at the start of a line.
const RUNG_SEPARATOR = `${NBSP}/ `;
// The count of left-out places gets a line to itself. Run on after the last rung it splits wherever
// the line happens to end ("… / 1 / +5", then a lone "more"); glued together with non-breaking
// spaces it is wider than the narrowest tile and breaks mid-word instead.
const COUNT_SEPARATOR = `${NBSP}/\n`;

/**
 * `editor` is the dense tile used inside the tour editor's fixed-column rows;
 * `round` is the roomier tile the single-round selection screen lays out with
 * percentage widths in a wrapping grid. They differ only in spacing/typography.
 */
export type GameTileVariant = 'editor' | 'round';

type Props = {
  game: GameDefinition;
  selected: boolean;
  /** Media page path used for game-artwork requests, e.g. `/tour/[tourId]/edit`. */
  mediaPagePath: string;
  variant?: GameTileVariant;
  locked?: boolean;
  lockedLabel?: string;
  onPress: () => void;
  onInfoPress: () => void;
  /**
   * What each finishing position pays, as the backend resolved it for the weight in force. The
   * tile formats it itself: it has to know the rungs to wrap between them and to count the ones
   * it leaves out.
   */
  meritLadder?: number[];
  perTeam?: boolean;
  perAchievement?: boolean;
  perEvent?: boolean;
  weightPercent?: number;
  weightLocked?: boolean;
  /**
   * The game pays a flat merit (hazard, achievement, per-event) that no weight changes, so the
   * tile shows what it pays without a weight control.
   */
  weightFixed?: boolean;
  /**
   * Caption naming the chosen rule set. Callers pass it only when the choice differs from the
   * definition's default — otherwise every tile grows a line that says nothing.
   */
  ruleSetLabel?: string;
  /** Fallback display name when neither i18n nor the definition provides one. */
  nameFallback?: string;
  /** Optional content rendered between the title and the artwork (e.g. a "recommended" ribbon). */
  badge?: ReactNode;
  /** Dims the tile — used for games that aren't recommended for the current field size. */
  deemphasised?: boolean;
  tileStyle?: StyleProp<ViewStyle>;
  imageStyle?: StyleProp<ImageStyle>;
};

export default function GameTile({
  game,
  selected,
  mediaPagePath,
  variant = 'editor',
  locked = false,
  lockedLabel,
  onPress,
  onInfoPress,
  meritLadder = [],
  perTeam = false,
  perAchievement = false,
  perEvent = false,
  weightPercent,
  weightLocked = false,
  weightFixed = false,
  ruleSetLabel,
  nameFallback,
  badge,
  deemphasised = false,
  tileStyle,
  imageStyle,
}: Props) {
  const variantStyles = variant === 'round' ? roundStyles : editorStyles;
  const imageSource = createGameImageResolver(mediaPagePath)(game.image, game.name);
  const displayName = getGameDisplayName(game, nameFallback);
  const showMerits = selected && meritLadder.length > 0 && typeof weightPercent === 'number';
  // Every rung the tile has room for, then a count standing in for the rest — never an ellipsis,
  // which says a ladder was cut without saying by how much.
  const { shown: shownRungs, hiddenCount } = clipMeritLadder(meritLadder, MAX_TILE_LADDER_RUNGS);
  const shownLadder = formatMeritLadder(shownRungs, RUNG_SEPARATOR);
  const ladderText =
    hiddenCount > 0
      ? [shownLadder, t('selectGames.meritLadderMore', { count: hiddenCount })].join(COUNT_SEPARATOR)
      : shownLadder;
  // The caption earns its line only when it says something the ladder does not: that the tour is
  // setting the weight, or what kind of merit this is ("per team"). A plain "merits" under a row
  // of merits is a word for its own sake.
  const captionKey = weightLocked && !weightFixed
    ? 'selectGames.meritWeightSetByTour'
    : perTeam || perAchievement || perEvent
      ? meritsCaptionKey({ perTeam, perAchievement, perEvent })
      : null;

  return (
    // testIDs are derived from the game id rather than taken as a prop, so every screen using a
    // GameTile (round select-games and the tour-edit game editors) is instrumented identically.
    // Selection state is on accessibilityState, so a test asserts "is this game selected" through
    // the checkbox role instead of inspecting styles.
    <Pressable
      testID={`game-tile-${game.id}`}
      onPress={onPress}
      style={[
        styles.gameTile,
        variantStyles.gameTile,
        selected && styles.gameTileSelected,
        selected && variantStyles.gameTileSelected,
        deemphasised && styles.gameTileDeemphasised,
        locked && styles.gameTileLocked,
        tileStyle,
      ]}
      accessibilityRole="checkbox"
      // react-native-web 0.21 no longer maps accessibilityState to aria-*, so the tile was
      // rendering role="checkbox" with NO aria-checked — a checkbox that never tells a screen
      // reader whether it is ticked. aria-* props are supported on both platforms (RN 0.81),
      // and accessibilityState stays for older native paths.
      aria-checked={selected}
      aria-disabled={locked}
      accessibilityState={{ checked: selected, disabled: locked }}
    >
      <Text
        style={[styles.gameTileName, variantStyles.gameTileName]}
        numberOfLines={variant === 'editor' ? 1 : undefined}
      >
        {displayName}
      </Text>
      {badge}
      <View style={styles.gameImageWrap}>
        <Image
          source={imageSource}
          style={[styles.gameImage, variantStyles.gameImage, selected && styles.gameImageSelected, imageStyle]}
          resizeMode="cover"
        />
        {selected ? (
          <View style={styles.selectedCheckBadge}>
            <Text style={styles.selectedCheckText}>✓</Text>
          </View>
        ) : null}
        <Pressable
          testID={`game-tile-info-${game.id}`}
          style={styles.infoButton}
          onPress={onInfoPress}
          accessibilityRole="button"
          accessibilityLabel={t('selectGames.gameInfoSetup')}
        >
          <Text style={styles.infoButtonText}>{t('selectGames.infoSetupIcon')}</Text>
        </Pressable>
      </View>
      {showMerits ? (
        <View testID={`game-tile-merits-${game.id}`} style={styles.meritRow}>
          {weightFixed ? null : (
            // Read-only here: the weight is set in the game setup modal behind the tile's
            // info-and-setup button, so one tap on the tile means "pick this game" and nothing else.
            <GameWeightControl
              testID={`game-tile-weight-${game.id}`}
              weightPercent={weightPercent}
              selected
              showLabel={false}
              size="compact"
              // Always the whole ladder, however much of it the tile below has room to print.
              accessibilityLabel={t('selectGames.meritLadderA11y', {
                name: displayName,
                ladder: formatMeritLadder(meritLadder),
                weight: weightPercent,
              })}
            />
          )}
          <View style={styles.meritTextWrap}>
            {/* No numberOfLines: a ladder has a rung per player, and clamping it to a line turned
                twelve rungs into "8 / 7 / 5 / 4 /…". It wraps instead, and clipMeritLadder above is
                what bounds the height. */}
            <Text testID={`game-tile-ladder-${game.id}`} style={[styles.meritLadderText, variantStyles.meritLadderText]}>
              {ladderText}
            </Text>
            {captionKey ? (
              // Not clamped either: "merits per team player" is wider than the box beside the
              // weight chip on a phone, and an ellipsis would cut the words that qualify the merits.
              <Text style={[styles.meritCaption, weightLocked && !weightFixed && styles.meritCaptionLocked]}>
                {t(captionKey)}
              </Text>
            ) : null}
          </View>
        </View>
      ) : null}
      {selected && ruleSetLabel ? (
        <Text testID={`game-tile-rule-${game.id}`} style={styles.gameTileRuleLabel} numberOfLines={1}>
          {ruleSetLabel}
        </Text>
      ) : null}
      {locked && lockedLabel ? <Text style={styles.gameTileLockedLabel}>{lockedLabel}</Text> : null}
    </Pressable>
  );
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 3px 9px rgba(0,0,0,0.2)' },
  default: {
    shadowColor: appColors.text.secondary,
    shadowOpacity: 0.2,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
  },
});

const styles = StyleSheet.create({
  gameTile: {
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 8,
    minHeight: 88,
    ...cardShadow,
  },
  gameTileSelected: {
    borderColor: appColors.border.accent,
  },
  gameTileDeemphasised: {
    opacity: 0.84,
    borderColor: appColors.border.default,
  },
  gameTileLocked: {
    borderColor: appColors.status.warningBorder,
    backgroundColor: appColors.surface.brownDeep,
  },
  gameImageWrap: {
    position: 'relative',
  },
  gameImage: {
    width: '100%',
    borderRadius: 10,
  },
  gameImageSelected: {
    opacity: 0.6,
  },
  infoButton: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: appColors.surface.light,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
  },
  infoButtonText: {
    color: appColors.surface.tintDeep,
    fontSize: 22,
    fontWeight: '800',
  },
  selectedCheckBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    paddingHorizontal: 6,
    backgroundColor: appColors.status.successStrong,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
  },
  selectedCheckText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    lineHeight: 14,
  },
  gameTileName: {
    fontWeight: '700',
    color: appColors.text.primary,
    textAlign: 'center',
  },
  gameTileRuleLabel: {
    marginTop: 4,
    fontSize: 11,
    color: appColors.text.secondary,
    textAlign: 'center',
  },
  gameTileLockedLabel: {
    marginTop: 4,
    fontSize: 11,
    fontWeight: '700',
    color: appColors.status.warningText,
    textAlign: 'center',
  },
  meritRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
  },
  meritTextWrap: {
    flexShrink: 1,
  },
  meritLadderText: {
    fontWeight: '700',
    color: appColors.text.primary,
  },
  meritCaption: {
    fontSize: 11,
    color: appColors.text.muted,
  },
  meritCaptionLocked: {
    color: appColors.status.warningText,
  },
});

const editorStyles = StyleSheet.create({
  gameTile: {
    flex: 1,
    overflow: 'hidden',
  },
  gameTileSelected: {
    borderWidth: 2,
  },
  gameTileName: {
    fontSize: 14,
    marginBottom: 8,
  },
  // Square artwork inside the editor's fixed-column rows.
  gameImage: {
    aspectRatio: 1,
  },
  // Small enough that a fourball's ladder ("8 / 4 / 1 / 0") sits on one line beside the weight
  // chip; a longer one wraps onto the lines below it.
  meritLadderText: {
    fontSize: 11,
  },
});

const roundStyles = StyleSheet.create({
  gameTile: {},
  gameTileSelected: {},
  gameTileName: {
    fontSize: 15,
    marginBottom: 6,
  },
  // Height is overridden per breakpoint by the screen's imageStyle.
  gameImage: {
    height: 140,
  },
  meritLadderText: {
    fontSize: 13,
  },
});
